// Shared severity/category color system — Tailwind classes for JSX, hex values for Chart.js.

export const SEVERITY_COLORS = {
  low: {
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-500',
    hex: '#10b981',
  },
  medium: {
    text: 'text-amber-800',
    bg: 'bg-amber-50',
    border: 'border-amber-500',
    hex: '#f59e0b',
  },
  high: {
    text: 'text-rose-700',
    bg: 'bg-rose-50',
    border: 'border-rose-500',
    hex: '#e11d48',
  },
  critical: {
    text: 'text-rose-800',
    bg: 'bg-rose-100',
    border: 'border-rose-600',
    hex: '#be123c',
  },
  unknown: {
    text: 'text-slate-600',
    bg: 'bg-slate-100',
    border: 'border-slate-300',
    hex: '#94a3b8',
  },
};

export const CATEGORY_COLORS = {
  legal: {
    text: 'text-indigo-700',
    bg: 'bg-indigo-50',
    hex: '#4f46e5',
  },
  regulatory: {
    text: 'text-violet-700',
    bg: 'bg-violet-50',
    hex: '#7c3aed',
  },
  hr: {
    text: 'text-amber-800',
    bg: 'bg-amber-50',
    hex: '#f59e0b',
  },
  criminal: {
    text: 'text-rose-700',
    bg: 'bg-rose-50',
    hex: '#e11d48',
  },
  financial: {
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    hex: '#10b981',
  },
  compliance: {
    text: 'text-sky-700',
    bg: 'bg-sky-50',
    hex: '#0ea5e9',
  },
  unknown: {
    text: 'text-slate-600',
    bg: 'bg-slate-100',
    hex: '#94a3b8',
  },
};

// Backend returns severity/category in inconsistent casing (e.g. "CRITICAL"); normalize
// before every lookup/comparison so filtering, sorting, and color mapping all agree.
export function normalizeSeverity(severity) {
  return (severity || 'unknown').toLowerCase();
}

export function normalizeCategory(category) {
  return (category || 'unknown').toLowerCase();
}

export function getSeverityColor(severity) {
  return SEVERITY_COLORS[normalizeSeverity(severity)] || SEVERITY_COLORS.unknown;
}

export function getCategoryColor(category) {
  return CATEGORY_COLORS[normalizeCategory(category)] || CATEGORY_COLORS.unknown;
}

// Deterministic color per company name, drawn from a fixed palette — lets 40+ distinct
// companies get a stable, visually distinct tag without hardcoding each one.
const COMPANY_PALETTE = [
  { text: 'text-blue-700', bg: 'bg-blue-50' },
  { text: 'text-purple-700', bg: 'bg-purple-50' },
  { text: 'text-emerald-700', bg: 'bg-emerald-50' },
  { text: 'text-orange-700', bg: 'bg-orange-50' },
  { text: 'text-pink-700', bg: 'bg-pink-50' },
  { text: 'text-cyan-700', bg: 'bg-cyan-50' },
  { text: 'text-indigo-700', bg: 'bg-indigo-50' },
  { text: 'text-amber-700', bg: 'bg-amber-50' },
  { text: 'text-rose-700', bg: 'bg-rose-50' },
  { text: 'text-teal-700', bg: 'bg-teal-50' },
  { text: 'text-violet-700', bg: 'bg-violet-50' },
  { text: 'text-lime-700', bg: 'bg-lime-50' },
];

function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function getCompanyColor(name) {
  if (!name) return COMPANY_PALETTE[0];
  return COMPANY_PALETTE[hashString(name) % COMPANY_PALETTE.length];
}
